import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { accessRefused, ApiError, api, READ_ONLY } from "../api/client";
import { onServerNotices } from "../api/notices";
import type {
  Annotation,
  CorridorModel,
  Deployment,
  Exercise,
  LineReanchor,
  Phase,
  Placement,
  PlacementAnchored,
  PlacementMeasures,
  PlacementMissions,
  PlacementOrientation,
  PlacementPosture,
  PlacementComposition,
  EniRole,
  TacticalGraphics,
  UnitRole,
} from "../api/types";
import { t, tc, tServer } from "../i18n/i18n";
import { type DeploymentParams, type Notice, useUi } from "../state/ui";
import {
  type History,
  type HistoryChannel,
  type HistoryChannelKey,
  type HistoryContext,
  history as mapHistory,
  isConflict,
  sessionRefusal,
  sameState,
} from "./history";
import { resetLines } from "./manoeuvre/corridorEdit";
import { refuseIfDossierLocked } from "../sharing/access";
import { orderActions, refreshOrders, withOrders } from "./orderSync";
import { handleZoneRefusal } from "./zones/zoneAttention";
import { deploymentKey, effectiveOpo } from "./units/useDeployment";

/** Écritures du dossier depuis la carte (docs/14 §2.8, K14) : une seule portée de mutation par dossier
 *  (`dossier-<id>`), partagée par toutes les écritures (déplacements, LIMA, fuseau, missions, lignes de la ZO,
 *  graphiques, ponts, potentiel) et par les envois de l'historique, pour que deux écritures ne se doublent jamais (chacune part avec
 *  la version laissée par la précédente : plus de 412 entre canaux) ; instantanés normalisés des quatre canaux de
 *  l'historique ; mise en cache commune des réponses ; conflits (412 / 409) : historique remis à zéro, dossier
 *  rechargé, avis. Les hooks d'écriture (useUnitMoves, useGraphics, useBridgeState, useManoeuvreWrites) passent
 *  tous par `dossierWrite` et branchent l'historique par `useDossierHistory`. */

// --- Instantanés des canaux ---------------------------------------------------------------------------------

/** État du canal « dispositif » (`PUT /placement`), normalisé : `corridor`, `missions`, `anchored`, `measures` et
 *  `orientation` toujours présents, null s'ils manquent (côté serveur, un champ absent veut dire « conservé ») ; ponts
 *  détruits, graphiques et potentiel exclus (ils ont leur canal). Assignable à `Placement`. Les lignes de la ZO
 *  (`PUT /measures`, docs/15 §3.8) s'inscrivent dans ce canal : leur annulation renvoie l'état complet. */
export interface DispositifState {
  positions: Placement["positions"];
  roles_phase: Phase | null;
  lines: Placement["lines"];
  opo_positions: Record<string, Record<string, [number, number]>>;
  corridor: CorridorModel | null;
  missions: PlacementMissions | null;
  anchored: PlacementAnchored | null;
  measures: PlacementMeasures | null;
  /** Rôles choisis dans le dossier pour les unités de l'ordre de bataille ennemi (docs/14 §3.4, §2.8). */
  eni_roles: Record<string, EniRole> | null;
  /** Sens de la manœuvre AMI → ENI (docs/14 §2.14) ; null : sens par défaut. */
  orientation: PlacementOrientation | null;
  /** Composition propre au dossier (docs/18 §1.1) : unités retirées et ajoutées ; null : celle de l'ordre de bataille. */
  composition: PlacementComposition | null;
  /** Rôles choisis pour les unités amies (docs/18 §1.2) ; null : aucun. */
  ami_roles: Record<string, UnitRole> | null;
  /** Posture tirée des missions (docs/25 §5) ; null : celle de la mission des Données initiales. */
  posture: PlacementPosture | null;
}

/** État des déplacements tel qu'il ressort d'un déploiement : celui du dossier s'il est fourni, sinon positions
 *  « déplacées » et lignes glissées relevées sur le déploiement. Toujours normalisé (clés explicites).
 *  Fuseau : celui qui est effectivement lu. Un fuseau stocké que le serveur ne lit plus (`corridor.custom` faux :
 *  grille ou règles changées, fuseau automatique affiché à la place) vaut null, comme le retrait silencieux du
 *  serveur ; le renvoyer tel quel ferait refuser (422) chaque annulation et le retour au dispositif doctrinal. */
export function placementOf(deployment: Deployment): DispositifState {
  const stored = deployment.placement;
  if (stored) {
    return {
      positions: stored.positions ?? {},
      roles_phase: stored.roles_phase ?? null,
      lines: stored.lines ?? {},
      opo_positions: stored.opo_positions ?? {},
      corridor: deployment.corridor?.custom ? (stored.corridor ?? null) : null,
      missions: stored.missions ?? null,
      anchored: stored.anchored ?? null,
      measures: stored.measures ?? null,
      eni_roles: stored.eni_roles ?? null,
      orientation: stored.orientation ?? null,
      composition: stored.composition ?? null,
      ami_roles: stored.ami_roles ?? null,
      posture: stored.posture ?? null,
    };
  }
  const positions: Placement["positions"] = {};
  for (const unit of deployment.units) {
    for (const [phase, pos] of Object.entries(unit.positions) as [Phase, (typeof unit.positions)[Phase]][]) {
      if (pos?.moved) (positions[unit.key] ??= {})[phase] = [pos.lon, pos.lat];
    }
  }
  const lines: Placement["lines"] = {};
  for (const line of deployment.corridor?.lines ?? []) if (line.moved) lines[line.name] = line.shift_m;
  return {
    positions,
    roles_phase: deployment.moves.roles_phase,
    lines,
    opo_positions: {},
    corridor: null,
    missions: null,
    anchored: null,
    measures: null,
    eni_roles: null,
    orientation: null,
    composition: null,
    ami_roles: null,
    posture: null,
  };
}

/** « Revenir au dispositif doctrinal » (docs/14 §2.2, docs/15 §3.8) : positions doctrinales (tous les OPO en
 *  CENTAC), missions doctrinales (`missions: null`), aucun ancrage (`anchored: null`), LIMA ramenées à `default_s`
 *  sans GDH saisi ni courbure, lignes de la ZO à leurs positions calculées (`measures: null`, CFL et FSCL
 *  rétablies) ; la géométrie du fuseau personnalisé est gardée (elle a son propre bouton), comme les rôles choisis
 *  pour les unités de l'ordre de bataille ennemi (un choix de composition, pas un déplacement) ; ses unités posées
 *  depuis la liste redeviennent « non placées » (leurs positions sont effacées avec les autres). */
export function doctrinalPlacement(deployment: Deployment): DispositifState {
  // Le sens de la manœuvre est gardé (docs/14 §2.14) : un choix du dispositif, pas un déplacement. La composition du
  // dossier et les rôles choisis aussi (docs/18 §6.2) : des choix de composition ; les unités ajoutées posées depuis
  // la liste redeviennent « à placer ».
  const { corridor, eni_roles, orientation, composition, ami_roles } = placementOf(deployment);
  return {
    positions: {},
    roles_phase: null,
    lines: {},
    opo_positions: {},
    corridor: corridor ? resetLines(corridor) : null,
    missions: null,
    anchored: null,
    measures: null,
    eni_roles,
    orientation,
    composition,
    ami_roles,
    // Missions doctrinales : la posture redevient celle de la mission des Données initiales.
    posture: null,
  };
}

/** État des déplacements avec les positions réancrées après le glissement d'une LIMA (`Deployment.reanchor`) :
 *  positions proposées écrites comme des déplacements à la main, ancrages de ces temps retirés. */
export function reanchoredPlacement(deployment: Deployment, plan: LineReanchor): DispositifState {
  const state = placementOf(deployment);
  const positions: Placement["positions"] = { ...state.positions };
  let anchored = state.anchored ? { ...state.anchored } : null;
  for (const [key, perPhase] of Object.entries(plan.positions)) {
    positions[key] = { ...(positions[key] ?? {}), ...perPhase };
    const phases = Object.keys(perPhase);
    if (anchored?.[key]) {
      const kept = anchored[key]!.filter((phase: Phase) => !phases.includes(phase));
      if (kept.length) anchored[key] = kept;
      else delete anchored[key];
    }
  }
  if (anchored && !Object.keys(anchored).length) anchored = null;
  return { ...state, positions, anchored };
}

/** Graphiques tactiques du dossier (état complet, trois familles toujours présentes). */
export function graphicsOf(deployment: Deployment | null | undefined): TacticalGraphics {
  const stored = deployment?.placement?.graphics;
  return {
    operationalGraphics: stored?.operationalGraphics ?? [],
    lineGraphics: stored?.lineGraphics ?? [],
    cbrnPlumes: stored?.cbrnPlumes ?? [],
  };
}

/** Annotations APP-6 du dossier (état complet, dans l'ordre enregistré). */
export function annotationsOf(deployment: Deployment | null | undefined): Annotation[] {
  return [...(deployment?.placement?.annotations?.items ?? [])];
}

/** Ponts détruits du dossier (clés triées : l'ordre ne compte pas). */
export function bridgesOf(deployment: Deployment | null | undefined): string[] {
  return [...(deployment?.placement?.destroyed_bridges ?? [])].sort();
}

/** Potentiel des unités (clé → 0 à 1,1 ; absente : nominal). */
export function strengthsOf(deployment: Deployment | null | undefined): Record<string, number> {
  return { ...(deployment?.placement?.strengths ?? {}) };
}

/** Instantané de chaque canal de l'historique, relevé sur un déploiement reçu. */
export const CHANNEL_SNAPSHOTS: { [K in HistoryChannelKey]: (deployment: Deployment) => unknown } = {
  dispositif: placementOf,
  graphiques: graphicsOf,
  ponts: bridgesOf,
  potentiel: strengthsOf,
  annotations: annotationsOf,
};

// --- Portée, contexte et mise en cache ---------------------------------------------------------------------

/** Portée de mutation unique du dossier : toutes ses écritures passent l'une après l'autre. */
export function dossierScope(exerciseId: string | null | undefined): { id: string } {
  return { id: `dossier-${exerciseId ?? "aucun"}` };
}

/** Contexte d'une écriture, relu au moment où elle part (et non au rendu du composant qui l'a demandée). */
export interface WriteContext {
  params: DeploymentParams | null;
  /** OPO qui compte (celui affiché en CENTAC, 1 ailleurs). */
  opo: number;
  key: ReturnType<typeof deploymentKey>;
  /** Dossier enregistré dont le brouillon n'est pas modifié ; null : écriture impossible. */
  exerciseId: string | null;
}

export function writeContext(): WriteContext {
  const { deploymentParams: params, opo } = useUi.getState();
  const effective = effectiveOpo(params, opo);
  return { params, opo: effective, key: deploymentKey(params, effective), exerciseId: params?.exercise_id ?? null };
}

/** Refus local « dossier non enregistré » : 428 (et non 409, réservé au conflit qui remet l'historique à zéro). */
export function notSavedError(message: string): ApiError {
  return new ApiError(428, message);
}

/** Déploiement reçu mis en cache, version du dossier reportée dans la liste ; `orders` : l'ordre a pu changer
 *  (pas pour les graphiques ni le potentiel). Les autres OPO (CENTAC) se recalculeront à leur prochain affichage. */
export function storeDeployment(queryClient: QueryClient, ctx: WriteContext, result: Deployment, orders = true): void {
  const before = queryClient.getQueryData<Deployment>(ctx.key);
  queryClient.setQueryData(ctx.key, result);
  // Composition, rôles ou unités du dispositif changés (écriture, annulation, docs/18) : l'ordre de bataille de la carte
  // (arbre des unités amies et ennemies, unités ajoutées, retirées, posées depuis la liste) est relu.
  if (before && compositionSignature(before) !== compositionSignature(result)) {
    void queryClient.invalidateQueries({ queryKey: ["orbat"] });
  }
  void queryClient.invalidateQueries({
    queryKey: ["deployment", ctx.params],
    predicate: (q) => q.queryKey[2] !== ctx.opo,
  });
  queryClient.setQueryData<Exercise[]>(["exercises"], (list) =>
    list?.map((e) =>
      e.id === ctx.exerciseId && result.exercise_version != null ? { ...e, version: result.exercise_version } : e,
    ),
  );
  if (orders) refreshOrders(queryClient, ctx.exerciseId);
  // OPO validé remis en rédaction par l'écriture (son texte a changé) : l'état des ordres de la liste des dossiers
  // (barre latérale, menu du dossier complet) est relu.
  if (result.orders?.reopened?.length) void queryClient.invalidateQueries({ queryKey: ["exercises"] });
}

/** Empreinte de la composition propre au dossier, des rôles choisis et des unités du dispositif (unités ajoutées,
 *  retirées, rôles, unité posée depuis la liste ou retirée de la carte) : l'arbre de l'ordre de bataille en dépend. */
export function compositionSignature(deployment: Deployment): string {
  const placement = deployment.placement;
  const units = deployment.units.map((u) => u.key).sort();
  return JSON.stringify([placement?.composition ?? null, placement?.ami_roles ?? null, placement?.eni_roles ?? null, units]);
}

/** Exécute `fn` dans la portée de mutation du dossier : après les écritures déjà en file, avant les suivantes. */
export function runInDossierScope<T>(
  queryClient: QueryClient,
  exerciseId: string,
  fn: () => Promise<T>,
): Promise<T> {
  const mutation = queryClient
    .getMutationCache()
    .build<T, unknown, void, unknown>(queryClient, { scope: dossierScope(exerciseId), mutationFn: fn, retry: false });
  return mutation.execute(undefined);
}

/** Envoi d'une requête versionnée (If-Match = version du déploiement en cache) dont la réponse est mise en cache. */
export type Send = (
  request: (target: Pick<Exercise, "id" | "version">, opo: number, current: Deployment) => Promise<Deployment>,
) => Promise<Deployment>;

/** Une ou plusieurs requêtes dans la portée du dossier, chacune avec la version laissée par la précédente. */
async function inScope<T>(
  queryClient: QueryClient,
  ctx: WriteContext,
  notSaved: string,
  orders: boolean,
  body: (send: Send, current: Deployment) => Promise<T>,
): Promise<T> {
  const exerciseId = ctx.exerciseId;
  if (!exerciseId) throw notSavedError(notSaved);
  return runInDossierScope(queryClient, exerciseId, async () => {
    const first = queryClient.getQueryData<Deployment>(ctx.key);
    if (first?.exercise_version == null) throw notSavedError(notSaved);
    const send: Send = async (request) => {
      const current = queryClient.getQueryData<Deployment>(ctx.key);
      if (current?.exercise_version == null) throw notSavedError(notSaved);
      const result = await request({ id: exerciseId, version: current.exercise_version }, ctx.opo, current);
      storeDeployment(queryClient, ctx, result, orders);
      return result;
    };
    return body(send, first);
  });
}

/** Résultat d'une écriture : réponse, déploiement d'avant, instantanés d'avant et d'après du canal. */
export interface DossierWrite {
  result: Deployment;
  previous: Deployment;
  channel: HistoryChannelKey | null;
  before: unknown;
  after: unknown;
}

export interface WriteOptions {
  /** Message si le dossier n'est pas enregistré (déjà traduit). */
  notSaved: string;
  /** Canal de l'historique où l'action s'inscrit (`recordWrite`) ; aucun : action hors historique. */
  channel?: HistoryChannelKey;
  /** L'ordre a pu changer (défaut : oui). */
  orders?: boolean;
}

/** Écriture du dossier depuis la carte : attend l'historique (`settle`, contrat de `HistoryChannel` : les
 *  annulations en attente partent d'abord), puis passe dans la portée du dossier, avec la version du déploiement
 *  en cache au moment où elle part ; la réponse est mise en cache avant que l'écriture suivante ne parte. */
export async function dossierWrite(
  queryClient: QueryClient,
  request: (target: Pick<Exercise, "id" | "version">, opo: number, current: Deployment) => Promise<Deployment>,
  options: WriteOptions,
  engine: Pick<History, "settle"> = mapHistory,
): Promise<DossierWrite> {
  const ctx = writeContext();
  if (!ctx.exerciseId) throw notSavedError(options.notSaved);
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : rien ne part, le bandeau est désigné ; refus local de même
  // forme que celui du serveur (`writeFailure` ne dit rien de plus).
  if (refuseIfDossierLocked()) throw new ApiError(403, t("Dossier en lecture seule"), READ_ONLY);
  await engine.settle();
  const snapshot = options.channel ? CHANNEL_SNAPSHOTS[options.channel] : null;
  return inScope(queryClient, ctx, options.notSaved, options.orders ?? true, async (send, current) => {
    const result = await send(request);
    return {
      result,
      previous: current,
      channel: options.channel ?? null,
      before: snapshot ? snapshot(current) : null,
      after: snapshot ? snapshot(result) : null,
    };
  });
}

/** Inscrit une écriture dans l'historique ; vrai si une entrée a été ajoutée (rien si l'état n'a pas changé). */
export function recordWrite(write: DossierWrite, label: string, engine: Pick<History, "record"> = mapHistory): boolean {
  if (!write.channel || write.before == null || write.after == null || sameState(write.before, write.after)) return false;
  engine.record(write.channel, write.before, write.after, label);
  return true;
}

/** Action « Annuler » d'un avis : elle défait la dernière action par l'historique (K14). */
export function undoAction(recorded: boolean, engine: Pick<History, "undo"> = mapHistory): Notice["action"] {
  return recorded ? { label: tc("défaire", "Annuler"), run: () => void engine.undo() } : undefined;
}

// --- Conflits ----------------------------------------------------------------------------------------------

/** Dossier rechargé (déploiements, liste des dossiers, ordres) après un conflit. */
export function reloadDossier(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ["deployment"] });
  void queryClient.invalidateQueries({ queryKey: ["exercises"] });
  refreshOrders(queryClient, useUi.getState().deploymentParams?.exercise_id ?? useUi.getState().exerciseId);
}

/** Refus d'une écriture de la carte. Refus propre à la zone libre (terrain en préparation, fuseau hors de la zone…) :
 *  avis `text` avec le message du serveur et l'action attendue désignée (`handleZoneRefusal`), historique gardé.
 *  Refus de session (comptes nominatifs) : rien, le portail des comptes s'en charge. 412 / 409 : le dossier a changé
 *  ailleurs, l'historique repart de zéro et le dossier est rechargé ; sinon avis `text` avec le message du serveur. */
export function writeFailure(
  queryClient: QueryClient,
  error: unknown,
  text: string,
  engine: Pick<History, "reset"> = mapHistory,
): void {
  const notify = useUi.getState().notify;
  if (handleZoneRefusal(queryClient, error, text)) return;
  // Session verrouillée, fermée ou ouverte pour un autre compte (docs/17 §8.2) : ni remise à zéro ni avis, le portail
  // des comptes pose sa surcouche ou recharge la page.
  if (sessionRefusal(error)) return;
  // Lecture seule du dossier (refus du serveur ou refus local) : le bandeau est désigné par la coque (avis la première
  // fois) ou par `dossierWrite` ; l'historique est gardé.
  if (accessRefused(error)) return;
  if (isConflict(error)) {
    engine.reset();
    notify({ text: t("Le dossier a été modifié ailleurs : historique remis à zéro"), tone: "warn" });
    reloadDossier(queryClient);
    return;
  }
  notify({ text, detail: error instanceof ApiError ? error.message : t("Serveur injoignable"), tone: "warn" });
}

// --- Canaux de l'historique --------------------------------------------------------------------------------

type Previewer = ((state: DispositifState | null) => void) | null;
let dispositifPreview: Previewer = null;
const dispositifWatchers = new Set<(state: DispositifState | null) => void>();

/** Aperçu immédiat du canal dispositif pendant plusieurs ⌘Z rapides (fuseau et LIMA rendus côté client, étape
 *  suivante : `useManoeuvre.working`) ; null : aucun aperçu (les unités suivent à la réponse). */
export function setDispositifPreview(preview: Previewer): void {
  dispositifPreview = preview;
}

/** Autre lecteur du même aperçu (lignes de la ZO, docs/15 §3.9), à côté de celui du fuseau ; rend son retrait. */
export function watchDispositifPreview(watcher: (state: DispositifState | null) => void): () => void {
  dispositifWatchers.add(watcher);
  return () => {
    dispositifWatchers.delete(watcher);
  };
}

function previewDispositif(state: DispositifState | null): void {
  dispositifPreview?.(state);
  for (const watcher of dispositifWatchers) watcher(state);
}

const APPLY_NOT_SAVED = () => t("Dossier non enregistré");

/** Canaux de l'historique, branchés sur le cache du déploiement affiché (relu à chaque appel). */
export function dossierChannels(queryClient: QueryClient): { [K in HistoryChannelKey]: HistoryChannel } {
  const snapshot = (key: HistoryChannelKey) => () => {
    const ctx = writeContext();
    const current = queryClient.getQueryData<Deployment>(ctx.key);
    if (!ctx.exerciseId || current?.exercise_version == null) return null;
    return CHANNEL_SNAPSHOTS[key](current);
  };
  // Les envois de l'historique passent dans la portée du dossier, sans attendre `settle` (ils en font partie).
  const apply = (orders: boolean, body: (send: Send, current: Deployment) => Promise<unknown>) =>
    inScope(queryClient, writeContext(), APPLY_NOT_SAVED(), orders, body);
  return {
    dispositif: {
      snapshot: snapshot("dispositif"),
      apply: (state) => apply(true, (send) => send((target, opo) => api.setPlacement(target, state as Placement, opo))),
      preview: (state) => previewDispositif(state as DispositifState | null),
      // Rôles ennemis choisis toujours explicites (docs/14 §2.8) : le premier ⌘Z après un premier changement de rôle
      // les retire (`eni_roles: null`), au lieu de les laisser « conservés ».
      normalize: normalizeDispositif,
    },
    graphiques: {
      snapshot: snapshot("graphiques"),
      apply: (state) =>
        apply(false, async (send) => {
          const result = await send((target, opo) => api.setGraphics(target, state as TacticalGraphics, opo));
          // Graphique sélectionné retiré par l'annulation : la fiche se ferme.
          const selection = useUi.getState().selection;
          if (selection?.layer === "graphic") {
            const graphics = graphicsOf(result);
            const ids = [...graphics.operationalGraphics, ...graphics.lineGraphics, ...graphics.cbrnPlumes].map((g) => g.id);
            if (!ids.includes(selection.id)) useUi.getState().select(null);
          }
        }),
    },
    ponts: {
      snapshot: snapshot("ponts"),
      // Un pont par requête (`PUT /bridges`) : seuls ceux qui diffèrent sont envoyés ; la dernière réponse est rendue
      // (avis de l'annulation complété de la mention des ordres).
      apply: (state) =>
        apply(true, async (send, current) => {
          const target = new Set(state as string[]);
          const now = new Set(bridgesOf(current));
          let last: Deployment | null = null;
          for (const key of new Set([...target, ...now])) {
            if (target.has(key) !== now.has(key)) {
              last = await send((ex, opo) => api.setBridge(ex, key, target.has(key), opo));
            }
          }
          return last;
        }),
    },
    annotations: {
      snapshot: snapshot("annotations"),
      // L'ordre suit la carte : la réponse peut porter des OPO recomposés (avis de l'annulation complété).
      apply: (state) =>
        apply(true, async (send) => {
          const result = await send((target, opo) => api.setAnnotations(target, state as Annotation[], opo));
          // Annotation sélectionnée retirée par l'annulation : la fiche se ferme.
          const selection = useUi.getState().selection;
          if (selection?.layer === "annotation" && !annotationsOf(result).some((a) => a.id === selection.id)) {
            useUi.getState().select(null);
          }
          return result;
        }),
    },
    potentiel: {
      snapshot: snapshot("potentiel"),
      // Une unité par requête (`PUT /strengths`) ; absente de l'état : potentiel nominal (null).
      apply: (state) =>
        apply(false, async (send, current) => {
          const target = state as Record<string, number>;
          const now = strengthsOf(current);
          for (const unit of new Set([...Object.keys(target), ...Object.keys(now)])) {
            if (target[unit] !== now[unit]) {
              await send((ex, opo) => api.setStrength(ex, unit, target[unit] ?? null, opo));
            }
          }
        }),
    },
  };
}

/** Instantané du canal dispositif avec `eni_roles` explicite (null s'il manque). */
export function normalizeEniRoles(state: unknown): unknown {
  if (!state || typeof state !== "object") return state;
  const roles = (state as { eni_roles?: unknown }).eni_roles;
  return { ...(state as object), eni_roles: roles ?? null };
}

/** Instantané du canal dispositif avec `eni_roles`, `composition` et `ami_roles` explicites (null s'ils manquent) :
 *  le premier ⌘Z après un premier ajout, retrait ou rôle choisi les retire au lieu de les laisser « conservés ». */
export function normalizeDispositif(state: unknown): unknown {
  const out = normalizeEniRoles(state);
  if (!out || typeof out !== "object") return out;
  const keys = out as { composition?: unknown; ami_roles?: unknown };
  return { ...(out as object), composition: keys.composition ?? null, ami_roles: keys.ami_roles ?? null };
}

/** Base du dispositif (même tuple que `_placement_basis` du serveur : camp, niveau, ODB, mission, rotation
 *  CENTAC), plus l'ordre de bataille ennemi (en changer retire ses rôles choisis et ses unités posées depuis la
 *  liste) : son changement remet l'historique à zéro. Sans ordre de bataille ennemi, la base est celle d'avant. */
export function placementBasis(params: DeploymentParams): string {
  const basis: unknown[] = [
    params.map_slug,
    params.level,
    params.odb,
    params.mission,
    params.level === "CENTAC" ? (params.rotation_deg ?? 0) : 0,
  ];
  if (params.eni_odb) basis.push(params.eni_odb);
  return JSON.stringify(basis);
}

/** Suit le contexte de l'historique (dossier, base du dispositif enregistrée, OPO en CENTAC) : la base n'est
 *  relevée que sur le déploiement du dossier enregistré, pas sur l'aperçu d'un brouillon. */
export function trackHistoryContext(engine: Pick<History, "setContext"> = mapHistory): () => void {
  let known: { dossier: string | null; basis: string | null } = { dossier: null, basis: null };
  const update = () => {
    const { exerciseId: dossier, deploymentParams: params, opo } = useUi.getState();
    if (dossier !== known.dossier) known = { dossier, basis: null };
    if (dossier && params?.exercise_id === dossier) known.basis = placementBasis(params);
    const context: HistoryContext = {
      dossier,
      basis: known.basis,
      opo: params?.level === "CENTAC" ? String(opo) : null,
    };
    engine.setContext(context);
  };
  update();
  return useUi.subscribe(update);
}

// --- Avis du serveur (`ExerciseOut.notices`) ---------------------------------------------------------------

/** Délai au-delà duquel les avis du serveur s'affichent seuls, et durée pendant laquelle ils rejoignent l'avis
 *  suivant (« Dossier enregistré », « Dossier importé ») au lieu d'être écrasés par lui. */
export const SERVER_NOTICE_ALONE_MS = 800;
export const SERVER_NOTICE_MERGE_MS = 4000;

/** Relais des avis du serveur vers la barre d'avis : ajoutés au détail de l'avis qui suit (l'enregistrement ou
 *  l'import qui les a produits), ou affichés seuls en ton `info`, traduits par `tServer`. */
export function relayServerNotices(): () => void {
  let pending: { texts: string[]; at: number } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let own = false;
  const notify = (notice: Omit<Notice, "id">) => {
    own = true;
    try {
      useUi.getState().notify(notice);
    } finally {
      own = false;
    }
  };
  const offNotices = onServerNotices((notices) => {
    pending = { texts: notices.map(tServer), at: Date.now() };
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      // Affichés seuls : ils ne rejoignent plus l'avis suivant (sans rapport avec eux).
      const texts = pending?.texts;
      pending = null;
      if (texts?.length) notify({ text: texts[0]!, detail: texts.slice(1).join(" ") || undefined, tone: "info" });
    }, SERVER_NOTICE_ALONE_MS);
  });
  const offUi = useUi.subscribe((state, previous) => {
    if (own || !pending || !state.notice || state.notice === previous.notice) return;
    if (Date.now() - pending.at > SERVER_NOTICE_MERGE_MS) {
      pending = null;
      return;
    }
    const { id: _id, ...notice } = state.notice;
    // Chaque phrase une seule fois, même si l'avis qui suit l'a déjà reprise.
    const texts = pending.texts.filter((text) => !notice.detail?.includes(text));
    pending = null;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    notify({ ...notice, detail: [notice.detail, ...texts].filter(Boolean).join(" ") });
  });
  return () => {
    offNotices();
    offUi();
    if (timer !== null) clearTimeout(timer);
  };
}

// --- Avis de l'historique ----------------------------------------------------------------------------------

/** Avis « Annulé : … » ou « Rétabli : … » complété à la réponse du serveur : mention des ordres (OPO mis à jour,
 *  inchangé, remis en rédaction…) et action vers l'ordre après « Rétablir » / « Annuler ». Null si la réponse ne
 *  touche pas l'ordre (graphiques, potentiel) ou si un autre avis a pris sa place entre-temps. */
export function completeHistoryNotice(notice: Omit<Notice, "id">, result: unknown): Omit<Notice, "id"> | null {
  if (!result || typeof result !== "object" || !("orders" in result)) return null;
  const deployment = result as Deployment;
  if (!deployment.orders && !deployment.outdated) return null;
  if (useUi.getState().notice?.text !== notice.text) return null;
  return {
    ...notice,
    detail: withOrders(notice.detail, deployment),
    ...orderActions(notice.action, deployment),
  };
}

// --- Branchement ------------------------------------------------------------------------------------------

let holders = 0;
let release: (() => void) | null = null;

/** Branche l'historique sur le dossier (quatre canaux, contexte, rechargement sur conflit, avis du serveur) tant
 *  qu'au moins un utilisateur le tient ; rend sa libération. Les canaux ne gardent aucun état propre : quel que
 *  soit le composant qui les tient, ils lisent le même cache. */
export function holdDossierHistory(queryClient: QueryClient, engine: History = mapHistory): () => void {
  holders++;
  if (holders === 1) {
    const channels = dossierChannels(queryClient);
    const offs = (Object.keys(channels) as HistoryChannelKey[]).map((key) => engine.registerChannel(key, channels[key]));
    engine.configure({
      onConflict: () => reloadDossier(queryClient),
      onZoneRefusal: (error, title) => handleZoneRefusal(queryClient, error, title),
      complete: completeHistoryNotice,
    });
    offs.push(trackHistoryContext(engine), relayServerNotices());
    release = () => offs.forEach((off) => off());
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders--;
    if (holders === 0) {
      release?.();
      release = null;
    }
  };
}

/** Hook des écritures de la carte : historique branché tant que le composant est monté. */
export function useDossierHistory(): void {
  const queryClient = useQueryClient();
  useEffect(() => holdDossierHistory(queryClient), [queryClient]);
}
